import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import PickedShipment from 'components/add_order/picklist/ProcessPicklist/PickedShipment';
import PicklistDetailSummary from 'components/add_order/picklist/PicklistDetail/PicklistDetailSummary';
import PicklistShipmentsPanel from 'components/add_order/picklist/ProcessPicklist/PicklistShipmentsPanel';
import PicklistProductsPanel from 'components/add_order/picklist/ProcessPicklist/PicklistProductsPanel';

import { picklistApi, shipmentApi } from 'api';
import paths from 'routes/paths';
import {
  convertPdfToHtml,
  showAwbPdf,
  generatePicklistPdf,
  generateBlueDartAwbPdf,
} from 'utils/pdfGenerators';

import { useUserContext } from 'providers/UserProvider';

const ProcessPicklist = () => {
  const { selectedTenant, selectedWarehouse } = useUserContext();
  const navigate = useNavigate();
  const { picklistId } = useParams();
  const [searchParams] = useSearchParams();
  const picklistCode = searchParams.get('picklist_code');

  const inputRef = useRef(null);

  const [pickedShipment, setPickedShipment] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showShipments, setShowShipments] = useState(false);
  const [showItems, setShowItems] = useState(false);

  const [inputType, setInputType] = useState('barcode'); // 'barcode' | 'shipment_code'
  const [inputValue, setInputValue] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  const notifyPicklistUpdated = useCallback(() => {
    window.dispatchEvent(new CustomEvent('picklist-updated'));
  }, []);

  const fetchPickedShipment = useCallback(async () => {
    try {
      setLoading(true);
      const response = await picklistApi.getPickedShipment(picklistId);
      setPickedShipment(response);
    } catch (err) {
      setPickedShipment(null);
    } finally {
      setLoading(false);
    }
  }, [picklistId]);

  const fetchShipmentOfPicklist = async (picklistId, shipmentId) => {
    try {
      const response = await picklistApi.getShipmentDetailOfPicklist(
        picklistId,
        shipmentId,
      );
      setPickedShipment(response);
    } catch (err) {
      setPickedShipment(null);
    }
  };

  useEffect(() => {
    if (!loading && !submitLoading) {
      inputRef.current?.focus();
    }
  }, [loading, submitLoading]);

  useEffect(() => {
    fetchPickedShipment();
  }, [picklistId, fetchPickedShipment]);

  useEffect(() => {
    if (!pickedShipment) {
      setInputType('barcode');
      return;
    }

    if (pickedShipment?.shipment_status === 'picked') {
      setInputType('barcode');
    }
  }, [pickedShipment]);

  const isShowSelectBox = () => {
    if (!pickedShipment) return true;

    return pickedShipment?.shipment_status !== 'picked';
  };

  const updateLocalShipmentState = (
    shipmentData,
    shipmentLineId,
    shipmentLineStatus,
    shipmentLineStatusLabel,
    newShipmentStatus,
    newShipmentStatusLabel,
  ) => {
    return {
      ...shipmentData,
      shipment_status: newShipmentStatus,
      shipment_status_label: newShipmentStatusLabel,
      lines: shipmentData.lines.map(line => {
        if (line.id === shipmentLineId) {
          return {
            ...line,
            shipment_line_status: shipmentLineStatus,
            shipment_line_status_label: shipmentLineStatusLabel,
          };
        }
        return line;
      }),
    };
  };

  const handleClosePicklist = async picklistId => {
    try {
      await picklistApi.picklistStatusToClosed(picklistId);
      toast.success('Picklist closed successfully!');
      const tenant = selectedTenant?.domains?.[0]?.domain;
      const warehouse = selectedWarehouse?.domain;
      navigate(paths.picklists.list(tenant, warehouse));
    } catch (err) {
      toast.error('Fail to close picklist');
    }
  };

  const handlePrintPicklistPdf = async (picklistId, picklistCode) => {
    try {
      const response = await picklistApi.getProductBatchesOfPicklist(
        picklistId,
      );
      generatePicklistPdf(response.results, picklistCode);
    } catch (error) {
      toast.error('Error generating PDF');
    }
  };

  const handlePrintInvoiceAndAWBPdf = async shipmentId => {
    try {
      const response = await shipmentApi.getInvoiceDetail(shipmentId);

      const shipmentAWBFile = response?.shipment?.awb_file;
      const isBlueDart =
        response?.shipment?.shipping_provider?.shipping_provider_type ===
        'BLUEDART';

      if (isBlueDart) {
        generateBlueDartAwbPdf(response);
      } else if (shipmentAWBFile) {
        const awbHtml = await convertPdfToHtml(shipmentAWBFile);
        showAwbPdf(awbHtml, response);
      }
    } catch (error) {
      toast.error('Error generating PDF');
    }
  };

  const handleInputSubmit = async e => {
    e.preventDefault();
    if (!inputValue) return;

    try {
      setSubmitLoading(true);

      if (inputType === 'shipment_code') {
        const response = await picklistApi.setShipmentStatusToPicked(
          picklistId,
          inputValue,
        );

        switch (response.shipment_status) {
          case 'picked':
            await fetchPickedShipment();
            break;

          case 'ready_to_ship':
            await fetchShipmentOfPicklist(picklistId, response.id);
            notifyPicklistUpdated();

            await handlePrintInvoiceAndAWBPdf(response.id);
            break;

          case 'packed':
            await fetchShipmentOfPicklist(picklistId, response.id);
            notifyPicklistUpdated();
            break;
        }
      } else if (inputType === 'barcode') {
        const response = await picklistApi.setShipmentLineStatusToPicked(
          picklistId,
          inputValue,
        );

        const shipmentStatus = response?.shipment.shipment_status;

        // 바코드 스캔 이후
        // picked shipment 없거나
        // 이전 shipment status  'ready_to_ship' || 'packed' 일 때 picklist shipment 상세 조회.
        // picked shipment 있다면 재조회 없이 클라이언트 상태로 관리
        if (
          !pickedShipment ||
          pickedShipment?.shipment_status === 'packed' ||
          pickedShipment?.shipment_status === 'ready_to_ship'
        ) {
          await fetchShipmentOfPicklist(picklistId, response?.shipment?.id);
        } else {
          setPickedShipment(prevShipment =>
            updateLocalShipmentState(
              prevShipment,
              response?.shipment_line?.id,
              response?.shipment_line?.shipment_line_status,
              response?.shipment_line?.shipment_line_status_label,
              response?.shipment.shipment_status,
              response?.shipment.shipment_status_label,
            ),
          );
        }

        if (shipmentStatus === 'ready_to_ship') {
          await handlePrintInvoiceAndAWBPdf(response.shipment.id);
        }

        if (shipmentStatus === 'ready_to_ship' || shipmentStatus === 'packed') {
          notifyPicklistUpdated();
        }
      }
    } catch (err) {
      toast.error('Fail to scan');
    } finally {
      setSubmitLoading(false);
      setInputValue('');
    }
  };

  return (
    <div className="d-flex flex-column">
      <div className="mt-1 mb-3 d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Picklist #{picklistCode || picklistId}</h5>
        <div className="d-flex gap-2">
          <Button
            variant="falcon-default"
            size="sm"
            onClick={() => setShowShipments(true)}
          >
            View Shipments
          </Button>
          <Button
            variant="falcon-default"
            size="sm"
            onClick={() => setShowItems(true)}
          >
            View Items
          </Button>
          <Button
            variant="falcon-default"
            size="sm"
            onClick={() => handleClosePicklist(picklistId)}
          >
            Close Picklist
          </Button>
          <Button
            variant="falcon-default"
            size="sm"
            onClick={() => handlePrintPicklistPdf(picklistId, picklistCode)}
          >
            <FontAwesomeIcon icon="file-alt" />
          </Button>
        </div>
      </div>

      <Row className="flex-grow-1">
        <Col md={9} className="mb-3 mb-md-0">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center p-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : pickedShipment ? (
            <PickedShipment
              picklistId={picklistId}
              shipmentData={pickedShipment}
              onRefresh={fetchPickedShipment}
              onPicklistUpdate={notifyPicklistUpdated}
            />
          ) : (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 py-5">
              <h2 className="text-700 mb-4">Start Packing</h2>
              <p className="text-500">
                Scan Barcode or Select Shipment to pack
              </p>
            </div>
          )}
        </Col>
        <Col md={3}>
          <PicklistDetailSummary
            picklistId={picklistId}
            showActionBtn={false}
            listenToUpdates={true}
          />
        </Col>
      </Row>

      <div className="fixed-bottom p-3 bg-white border-top">
        <Form onSubmit={handleInputSubmit} autoComplete="off">
          <Row className="align-items-center w-50">
            {isShowSelectBox() && (
              <Col xs="auto">
                <Form.Select
                  value={inputType}
                  onChange={e => setInputType(e.target.value)}
                  disabled={submitLoading || loading}
                >
                  <option value="barcode">Scan Barcode</option>
                  <option value="shipment_code">Scan Shipment Code</option>
                </Form.Select>
              </Col>
            )}
            <Col>
              <Form.Control
                ref={inputRef}
                placeholder={
                  inputType === 'shipment_code'
                    ? 'Scan Shipment Code'
                    : 'Scan Barcode'
                }
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                disabled={submitLoading || loading}
                autoComplete="off"
              />
            </Col>
          </Row>
        </Form>
      </div>

      <PicklistShipmentsPanel
        show={showShipments}
        onHide={() => setShowShipments(false)}
        picklistId={picklistId}
      />

      <PicklistProductsPanel
        show={showItems}
        onHide={() => setShowItems(false)}
        picklistId={picklistId}
      />
    </div>
  );
};

export default ProcessPicklist;
